import { useState } from "react";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Props {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  keyLabel?: string;
  valueLabel?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

/** Editable string->string map (add/remove rows). Used for priority mapping,
 *  custom field values, export field overrides, etc. */
export function KeyValueEditor({
  value,
  onChange,
  keyLabel = "Key",
  valueLabel = "Value",
  keyPlaceholder = "key",
  valuePlaceholder = "value",
}: Props) {
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const entries = Object.entries(value);

  const addRow = () => {
    const k = newKey.trim();
    if (!k) return;
    onChange({ ...value, [k]: newValue });
    setNewKey("");
    setNewValue("");
  };

  const removeKey = (k: string) => {
    const { [k]: _removed, ...next } = value;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{keyLabel}</TableHead>
            <TableHead>{valueLabel}</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={3}
                className="text-muted-foreground text-center text-sm"
              >
                No entries
              </TableCell>
            </TableRow>
          ) : (
            entries.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell className="font-mono text-xs">{k}</TableCell>
                <TableCell>
                  <Input
                    value={v}
                    onChange={(e) => { onChange({ ...value, [k]: e.target.value }); }}
                    className="h-8"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => { removeKey(k); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-end gap-2">
        <Input
          value={newKey}
          onChange={(e) => { setNewKey(e.target.value); }}
          placeholder={keyPlaceholder}
          className="h-8"
        />
        <Input
          value={newValue}
          onChange={(e) => { setNewValue(e.target.value); }}
          placeholder={valuePlaceholder}
          className="h-8"
        />
        <Button variant="outline" size="sm" onClick={addRow} disabled={!newKey.trim()}>
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}
